import { BoxOfferDraftEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { LoggerService } from "@modules/common/logger/loggerService"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { type BoxOfferApiGateway } from "@modules/boxOffer/gateway/boxOfferApi.gateway"
import {
  type BoxOfferDetailedDto,
  type BoxOfferInvitationDto,
  BoxOfferInvitationDtoSchema,
  type BoxOfferInvitationInput,
  type BoxOfferItemDto,
  BoxOfferItemDtoSchema,
  type BoxOfferMobileDetailedDto,
  BoxOfferMobileDetailedDtoSchema,
  BoxSubscriptionOfferSchema,
  DropInOfferSchema,
  MilleniumOfferSchema,
  type MobileBoxOfferItemDto,
  MobileBoxOfferItemDtoSchema,
  StatusFilter,
  TicketsBookOfferSchema,
  TrialOfferSchema,
} from "@modules/boxOffer/gateway/boxOfferDto"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { ConfigService } from "@modules/core/services/configService"

export class OfferApi implements BoxOfferApiGateway {
  private baseUrl = "/box/offers"

  constructor(private api: ApiClient) {}

  async getBoxOffers(options?: { onlyWithPrice?: boolean; statusFilter?: StatusFilter }): Promise<BoxOfferItemDto[]> {
    const resultDto = await this.api.get<BoxOfferItemDto[]>(`${this.baseUrl}`, {
      params: {
        onlyWithPrice: options?.onlyWithPrice,
        statusFilter: options?.statusFilter,
      },
    })
    return resultDto.data.map((dto) => LoggerService.parseApiDto(BoxOfferItemDtoSchema, dto))
  }

  async getBoxOffersByUser(boxId: string): Promise<MobileBoxOfferItemDto[]> {
    const resultDto = await this.api.get<MobileBoxOfferItemDto[]>(`/boxes/${boxId}/offers`)

    return resultDto.data.map((dto) => LoggerService.parseApiDto(MobileBoxOfferItemDtoSchema, dto))
  }

  async getBoxOfferByUser(boxId: string, offerId: string): Promise<BoxOfferMobileDetailedDto> {
    const resultDto = await this.api.get<BoxOfferMobileDetailedDto>(`/boxes/${boxId}/offers/${offerId}`)

    return LoggerService.parseApiDto(BoxOfferMobileDetailedDtoSchema, resultDto.data)
  }

  async getBoxOffer(id: string): Promise<BoxOfferDetailedDto> {
    const resultDto = await this.api.get<BoxOfferDetailedDto>(`${this.baseUrl}/${id}`)

    switch (resultDto.data.type) {
      case BoxOfferType.Subscription:
        return LoggerService.parseApiDto(BoxSubscriptionOfferSchema, resultDto.data)
      case BoxOfferType.TicketsBook:
        return LoggerService.parseApiDto(TicketsBookOfferSchema, resultDto.data)
      case BoxOfferType.DropIn:
        return LoggerService.parseApiDto(DropInOfferSchema, resultDto.data)
      case BoxOfferType.Millenium:
        return LoggerService.parseApiDto(MilleniumOfferSchema, resultDto.data)
      case BoxOfferType.Trial:
        return LoggerService.parseApiDto(TrialOfferSchema, resultDto.data)
    }
  }

  async createBoxOffer(offerDraft: BoxOfferDraftEntity): Promise<void> {
    await this.api.post<BoxOfferDetailedDto>(`${this.baseUrl}`, BoxOfferDraftEntity.toDto(offerDraft))
  }

  async updateBoxOffer(id: string, offerDraft: BoxOfferDraftEntity): Promise<void> {
    await this.api.put<BoxOfferDetailedDto>(`${this.baseUrl}/${id}`, BoxOfferDraftEntity.toDto(offerDraft))
  }

  async deleteBoxOffer(id: string): Promise<void> {
    await this.api.post<BoxOfferDetailedDto>(`${this.baseUrl}/${id}/archive`)
  }

  async sendBoxOfferInvitation(boxOfferInvitationInput: BoxOfferInvitationInput): Promise<void> {
    await this.api.post(`${this.baseUrl}/invite`, boxOfferInvitationInput)
  }

  async getBoxOfferInvitations(userId: string): Promise<BoxOfferInvitationDto[]> {
    const resultDto = await this.api.get<BoxOfferInvitationDto[]>(`${this.baseUrl}/invitations/${userId}`)
    return resultDto.data.map((dto) => LoggerService.parseApiDto(BoxOfferInvitationDtoSchema, dto))
  }

  async cancelBoxOfferInvitation(invitationId: string): Promise<BoxOfferInvitationDto> {
    const resultDto = await this.api.post<BoxOfferInvitationDto>(`${this.baseUrl}/invitations/${invitationId}/cancel`)
    return LoggerService.parseApiDto(BoxOfferInvitationDtoSchema, resultDto.data)
  }

  async subscribeFree(boxId: string, offerId: string): Promise<void> {
    await this.api.post(`/boxes/${boxId}/offers/${offerId}/obtain`)
  }

  async createBoxOfferPaymentSubscription(
    offerId: string,
    successUrl: string,
    cancelUrl: string,
  ): Promise<{ url: string }> {
    const result = await this.api.post<{ url: string }>(
      `${ConfigService.config.BASE_URL.replace("/web", "")}/mobile/stripe/create-box-offer-checkout-session`,
      {
        boxOfferId: offerId,
        embedded: false,
        successUrl,
        cancelUrl,
      },
    )
    return result.data
  }

  async refuseOfferInvitation(boxOfferInvitationId: string) {
    const resultDto = await this.api.post<BoxOfferInvitationDto>(
      `${this.baseUrl}/invitations/${boxOfferInvitationId}/refuse`,
    )
    return LoggerService.parseApiDto(BoxOfferInvitationDtoSchema, resultDto.data)
  }

  async arrangeOffersDisplayOrder(orderMap: Record<string, number>): Promise<void> {
    await this.api.put(`${this.baseUrl}/arrangeOffersDisplayOrder`, { offerOrder: orderMap })
  }
}
